/**
 * Global Privacy Control (GPC).
 *
 * The privacy policy says: "We honor Global Privacy Control (GPC) signals from
 * your browser. When we detect a GPC signal, we automatically disable marketing
 * and non-essential tracking technologies." Until Sep 1 2026 nothing in the
 * codebase read the signal — the only occurrence of `globalPrivacyControl` was
 * a type declaration in vite-env.d.ts. A published CCPA/CPRA commitment the
 * product did not keep.
 *
 * This is the implementation. Two properties matter and both are deliberate:
 *
 *   1. GPC is a legally recognised opt-out signal, so it takes effect on the
 *      first page view without waiting for the banner. There is nothing to ask
 *      about: the browser has already answered.
 *   2. It is treated as a floor, not an override. GPC can only ever turn
 *      marketing off and doNotSell on. A visitor who later opts back in through
 *      the banner or the Do Not Sell page keeps that choice, because an explicit
 *      later choice by the same person is more specific than the browser
 *      default. `respectedGpcAt` records that we acted on it, which is what an
 *      auditor will ask for.
 *
 * @see https://globalprivacycontrol.org/
 */

const STORAGE_KEY = 'cookie-consent';
/** IAB US Privacy string: version 1, notice given, opted out of sale, no LSPA. */
const USP_OPTED_OUT = '1YYN';

export type ConsentRecord = {
  essential?: boolean;
  functional?: boolean;
  analytics?: boolean;
  marketing?: boolean;
  doNotSell?: boolean;
  timestamp?: number;
  /** Set when a GPC signal was detected and applied. Audit trail. */
  respectedGpcAt?: number;
  /** Set when the visitor clicked the banner (Accept / Essential / Save). */
  bannerChosen?: boolean;
  /** Set when the visitor set a sale preference on /do-not-sell. Audit
   *  trail only: a sale opt-out is not a banner decision. */
  doNotSellAt?: number;
};

/** True when the browser is sending a GPC opt-out signal. */
export const gpcSignalPresent = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return navigator.globalPrivacyControl === true;
};

const readStored = (): ConsentRecord | null => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ConsentRecord) : null;
  } catch {
    return null;
  }
};

const setUSPrivacyCookie = () => {
  if (typeof document === 'undefined') return;
  document.cookie = `usprivacy=${USP_OPTED_OUT}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;
};

/**
 * Applies the GPC signal to the stored consent record, if one is being sent.
 *
 * Returns the record that should now be pushed into Consent Mode, or null when
 * there is no signal and nothing to do. Safe to call on every boot: applying it
 * twice is the same as applying it once.
 */
export const applyGpc = (): ConsentRecord | null => {
  if (!gpcSignalPresent()) return null;

  const stored = readStored();
  const next: ConsentRecord = {
    // Essential is always on. Do not invent analytics/functional denials —
    // those are banner choices. GPC only floors marketing and sale.
    essential: true,
    ...stored,
    marketing: false,
    doNotSell: true,
    respectedGpcAt: Date.now(),
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). The consent
    // push below still happens, so the signal is honoured for this session.
  }
  setUSPrivacyCookie();
  return next;
};

/**
 * Whether the visitor's opt-out came from GPC rather than from a click. The
 * Do Not Sell page uses this to explain why the toggle is already off.
 */
export const optedOutByGpc = (): boolean => {
  const stored = readStored();
  return Boolean(stored?.respectedGpcAt) && gpcSignalPresent();
};

/**
 * True when the visitor actually used the banner (or granted analytics).
 * A GPC-only floor write is not a choice. A Do Not Sell write that only
 * sets sale-opt-out is not a banner decision either — those records used
 * to look like essential-only and hid the Europe analytics grant UI.
 *
 * Do not compare `timestamp` to `respectedGpcAt`. applyGpc refreshes
 * `respectedGpcAt` on every boot and keeps an older timestamp, so that
 * equality is true only on the first write.
 */
export const hasExplicitConsentChoice = (): boolean => {
  const stored = readStored();
  if (!stored) return false;
  if (stored.bannerChosen === true) return true;
  if (stored.analytics === true) return true;
  // Before bannerChosen existed, banner and Do Not Sell submissions were
  // identifiable by their timestamp. Current GPC-only writes deliberately do
  // not add one, so old explicit choices remain distinguishable after GPC runs.
  if (typeof stored.timestamp === 'number') return true;
  return false;
};
